/**
 * "Added to shopping bag" popup.
 *
 * Canopy adds to cart with jQuery ($.post to /cart/add.js from theme.js), so
 * instead of owning the submit we listen to jQuery's global ajaxSuccess and
 * react to the response. The product row and the cart count are then re-rendered
 * through the Section Rendering API, which keeps metafields and translations in
 * Liquid rather than duplicating them here.
 */
class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = this.querySelector('#cart-notification');
    this.productContainer = this.querySelector('#cart-notification-product');
    this.buttonLabel = this.querySelector('#cart-notification-button');

    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onKeyUp = this.handleKeyUp.bind(this);

    this.querySelectorAll('[data-cart-notification-close]').forEach((button) => {
      button.addEventListener('click', this.close.bind(this));
    });

    this.listenForAddToCart();
  }

  // This script sits above vendor.js/theme.js in the document, so jQuery is not
  // defined yet when the element upgrades — wait for it rather than bailing out.
  listenForAddToCart(attempt = 0) {
    if (typeof window.jQuery === 'undefined') {
      if (attempt >= 100) return;
      setTimeout(() => this.listenForAddToCart(attempt + 1), 100);
      return;
    }

    window.jQuery(document).on('ajaxSuccess', (event, xhr, settings) => {
      if (!settings || typeof settings.url !== 'string') return;
      if (settings.url.indexOf('/cart/add') === -1) return;

      const item = xhr.responseJSON;
      if (!item || !item.key) return;

      this.renderContents(item.key);
    });
  }

  renderContents(itemKey) {
    const url = `${window.location.pathname}?sections=cart-notification-product,cart-notification-button`;

    fetch(url)
      .then((response) => response.json())
      .then((sections) => {
        const productHTML = sections['cart-notification-product'];
        if (productHTML) {
          const doc = new DOMParser().parseFromString(productHTML, 'text/html');
          const node = doc.getElementById(`cart-notification-product-${itemKey}`);
          this.productContainer.innerHTML = node ? node.outerHTML : '';
        }

        const buttonHTML = sections['cart-notification-button'];
        if (buttonHTML && this.buttonLabel) {
          const doc = new DOMParser().parseFromString(buttonHTML, 'text/html');
          const section = doc.querySelector('.shopify-section');
          this.buttonLabel.textContent = (section ? section.textContent : doc.body.textContent).trim();
        }

        this.open();
      })
      .catch(() => {
        // Section rendering failed — leave the theme's own cart drawer to it
      });
  }

  open() {
    // The theme opens its cart drawer on add for some cart types; only one of
    // the two should show up.
    document.body.classList.remove('show-cart-summary', 'product-added-to-cart');

    this.notification.classList.add('is-open');
    this.notification.focus({ preventScroll: true });

    document.addEventListener('click', this.onBodyClick);
    document.addEventListener('keyup', this.onKeyUp);
  }

  close() {
    this.notification.classList.remove('is-open');

    document.removeEventListener('click', this.onBodyClick);
    document.removeEventListener('keyup', this.onKeyUp);
  }

  handleBodyClick(event) {
    if (!event.target.closest('cart-notification')) this.close();
  }

  handleKeyUp(event) {
    if (event.code === 'Escape') this.close();
  }
}

customElements.define('cart-notification', CartNotification);
